import { useState } from 'react';
import { api } from '../lib/api.js';
import { Banner } from '../components/ui.jsx';
import { Entrance } from './Entrance.jsx';

export function TwoFactor({ pendingUser, onPassed }) {
  const [digits, setDigits] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [recovery, setRecovery] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  /*
   * One box, two kinds of code.
   *
   * The authenticator code is six digits, so this field strips everything else. That is
   * right until somebody pastes a recovery code into it, which is the obvious thing to do
   * because it is the box in front of them and the way to the other one is a button below
   * the fold of their attention. D57E7-A5AE8 then became 57758 with no explanation, and it
   * looked as though the code had turned into a number.
   *
   * A paste that carries a letter or a dash is a recovery code. Nothing else it could be.
   * So take it, switch to that mode, and put it where it belongs, rather than silently
   * destroying it and leaving the person to work out why.
   */
  function takeCode(raw) {
    if (/[A-Za-z-]/.test(raw)) {
      setUseRecovery(true);
      setRecovery(raw.trim().toUpperCase());
      setError(null);
      return;
    }
    setDigits(raw.replace(/\D/g, '').slice(0, 6));
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.twoFactor(useRecovery ? { recoveryCode: recovery } : { code: digits });
      onPassed();
    } catch (err) {
      setError(err.data?.locked
        ? `Too many attempts. Locked for ${err.data.minutes} minutes.`
        : useRecovery ? 'That recovery code is not valid or has been used.' : 'That code is not right. Codes change every 30 seconds.');
      setDigits('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Entrance>
      <form onSubmit={submit} style={{ display: 'grid', gap: 20 }}>
        <h1>Two factor</h1>
        <div className="lede">
          {useRecovery
            ? 'Enter one of the recovery codes issued when two factor was switched on. Each works once.'
            : `The six digit code from your authenticator app${pendingUser?.name ? `, ${pendingUser.name}` : ''}.`}
        </div>

        {!useRecovery ? (
          <input
            className="entrance-code"
            inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus
            value={digits}
            onChange={e => takeCode(e.target.value)} />
        ) : (
          <input
            className="entrance-code" autoFocus value={recovery}
            placeholder="XXXXX-XXXXX"
            style={{ fontSize: 20, letterSpacing: '0.1em' }}
            onChange={e => setRecovery(e.target.value.toUpperCase())} />
        )}

        <button className="btn-ezel" type="submit"
          disabled={busy || (useRecovery ? recovery.replace(/[^A-Z0-9]/gi, '').length < 10 : digits.length !== 6)}>
          {busy ? 'Checking' : 'Verify'}
        </button>

        <button type="button" className="btn-ezel-quiet"
          onClick={() => { setUseRecovery(v => !v); setError(null); }}>
          {useRecovery ? 'Use the authenticator app instead' : 'Use a recovery code instead'}
        </button>

        {error && <Banner tone="danger">{error}</Banner>}
      </form>
    </Entrance>
  );
}
